import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { organizationsAPI, hotelOpsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Layers, DoorClosed, ChevronLeft, Loader2, RefreshCw, Building2, History, Camera } from 'lucide-react';

const STATE_STYLE = {
    red: { dot: 'bg-rose-500', text: 'text-rose-600', label: 'Needs Attention' },
    orange: { dot: 'bg-amber-500', text: 'text-amber-600', label: 'Being Serviced / DND' },
    green: { dot: 'bg-emerald-500', text: 'text-emerald-600', label: 'Ready' },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

// Fetches an org-scoped staff-action photo (auth header via axios) as a blob URL.
const OpsPhoto = ({ orgId, photoId }) => {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        let active = true; let objUrl;
        hotelOpsAPI.photoBlob(orgId, photoId).then((r) => {
            objUrl = URL.createObjectURL(r.data);
            if (active) setUrl(objUrl);
        }).catch(() => {});
        return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
    }, [orgId, photoId]);
    if (!url) return <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center"><Camera className="w-4 h-4 text-muted-foreground" /></div>;
    return <img src={url} alt="" className="w-16 h-16 rounded-lg object-cover border" data-testid={`ops-photo-${photoId}`} />;
};

const StateBadge = ({ state }) => {
    const s = STATE_STYLE[state] || STATE_STYLE.red;
    return (
        <span className="inline-flex items-center gap-1.5" data-testid={`ops-state-${state}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
            <span className={`text-sm font-medium ${s.text}`}>{s.label}</span>
        </span>
    );
};

const HotelOpsPortal = () => {
    const { t } = useTranslation();
    const [orgs, setOrgs] = useState([]);
    const [orgId, setOrgId] = useState('');
    const [floors, setFloors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFloor, setSelectedFloor] = useState(null);
    const [detail, setDetail] = useState(null);
    const [activity, setActivity] = useState([]);
    const [history, setHistory] = useState(null); // {room_number, ...} modal

    useEffect(() => {
        organizationsAPI.list().then((r) => {
            setOrgs(r.data || []);
            if ((r.data || []).length) setOrgId(r.data[0].id);
        }).catch(() => {});
    }, []);

    const loadFloors = useCallback(async () => {
        if (!orgId) return;
        setLoading(true); setSelectedFloor(null); setDetail(null);
        try {
            const [f, a] = await Promise.all([hotelOpsAPI.floors(orgId), hotelOpsAPI.activity(orgId)]);
            setFloors(f.data.floors || []);
            setActivity(a.data.activity || []);
        } catch { toast.error('Failed to load Hotel Ops data'); }
        finally { setLoading(false); }
    }, [orgId]);

    useEffect(() => { loadFloors(); }, [loadFloors]);

    const openFloor = async (floor) => {
        setSelectedFloor(floor); setDetail(null);
        try { const r = await hotelOpsAPI.floorDetail(orgId, floor); setDetail(r.data); }
        catch { toast.error('Failed to load floor'); }
    };

    const openHistory = async (room_number) => {
        setHistory({ room_number, loading: true, items: [] });
        try {
            const r = await hotelOpsAPI.roomHistory(orgId, room_number);
            setHistory({ room_number, loading: false, ...r.data });
        } catch { setHistory({ room_number, loading: false, history: [] }); }
    };

    return (
        <div className="space-y-6" data-testid="hotel-ops-portal">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Building2 className="w-6 h-6 text-blue-600" /> {t('nav.hotelOps', 'Hotel Ops')}
                    </h1>
                    <p className="text-sm text-muted-foreground">{t('hotelOps.subtitle', 'Live floor & room operations across your hotel')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={orgId} onValueChange={setOrgId}>
                        <SelectTrigger className="w-56" data-testid="ops-org-select">
                            <SelectValue placeholder={t('hotelRooms.selectOrganization', 'Select organization')} />
                        </SelectTrigger>
                        <SelectContent>
                            {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={loadFloors} data-testid="ops-refresh">
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="floors">
                <TabsList>
                    <TabsTrigger value="floors" data-testid="ops-tab-floors">{t('hotelOps.floors', 'Floors')}</TabsTrigger>
                    <TabsTrigger value="activity" data-testid="ops-tab-activity">{t('hotelOps.activity', 'Staff Activity')}</TabsTrigger>
                </TabsList>

                <TabsContent value="floors" className="mt-4">
                    {loading ? (
                        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
                    ) : selectedFloor === null ? (
                        floors.length === 0 ? (
                            <p className="text-muted-foreground text-sm py-8 text-center" data-testid="ops-no-floors">{t('hotelOps.noFloors', 'No floors with rooms are configured yet. Add a room range under Rooms.')}</p>
                        ) : (
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {floors.map((f) => (
                                    <Card key={f.floor} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openFloor(f.floor)} data-testid={`ops-floor-card-${f.floor}`}>
                                        <CardContent className="p-5">
                                            <div className="flex items-center justify-between">
                                                <span className="flex items-center gap-2 font-semibold text-lg"><Layers className="w-5 h-5 text-indigo-500" /> {t('hotelRooms.floor', 'Floor')} {f.floor}</span>
                                                <Badge variant="outline">{f.total} {t('hotelOps.rooms', 'rooms')}</Badge>
                                            </div>
                                            <div className="flex gap-4 mt-4">
                                                <span className="flex items-center gap-1.5 text-sm" data-testid={`ops-floor-${f.floor}-red`}><span className="w-2.5 h-2.5 rounded-full bg-rose-500" /> {f.red}</span>
                                                <span className="flex items-center gap-1.5 text-sm" data-testid={`ops-floor-${f.floor}-orange`}><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> {f.orange}</span>
                                                <span className="flex items-center gap-1.5 text-sm" data-testid={`ops-floor-${f.floor}-green`}><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> {f.green}</span>
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-3">{t('hotelOps.lastActivity', 'Last activity')}: {fmt(f.last_activity)}</p>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )
                    ) : (
                        <div>
                            <button className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" onClick={() => { setSelectedFloor(null); setDetail(null); }} data-testid="ops-floor-back">
                                <ChevronLeft className="w-4 h-4" /> {t('hotelOps.allFloors', 'All floors')}
                            </button>
                            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Layers className="w-5 h-5 text-indigo-500" /> {t('hotelRooms.floor', 'Floor')} {selectedFloor}</h2>
                            {!detail ? (
                                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
                            ) : (
                                <div className="space-y-2">
                                    {detail.rooms.map((r) => (
                                        <Card key={r.room_number} data-testid={`ops-room-${r.room_number}`}>
                                            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                                                <div className="flex items-center gap-3 min-w-[160px]">
                                                    <DoorClosed className={`w-6 h-6 ${STATE_STYLE[r.state]?.text}`} />
                                                    <div>
                                                        <p className="font-semibold">{t('hotelOps.room', 'Room')} {r.room_number}</p>
                                                        <StateBadge state={r.state} />
                                                    </div>
                                                </div>
                                                <div className="text-xs text-muted-foreground flex-1 min-w-[200px]">
                                                    <p>{t('hotelOps.lastChange', 'Last change')}: {fmt(r.last_changed_at)} {r.last_changed_by ? `· ${r.last_changed_by}` : ''}</p>
                                                    <p>{t('hotelOps.lastCleaned', 'Last cleaned')}: {fmt(r.last_cleaned_at)} {r.last_cleaned_by ? `· ${r.last_cleaned_by}` : ''}</p>
                                                </div>
                                                <Button variant="ghost" size="sm" onClick={() => openHistory(r.room_number)} data-testid={`ops-room-history-${r.room_number}`}>
                                                    <History className="w-4 h-4 mr-1" /> {t('hotelOps.history', 'History')}
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                    {activity.length === 0 ? (
                        <p className="text-muted-foreground text-sm py-8 text-center" data-testid="ops-no-activity">{t('hotelOps.noActivity', 'No staff activity yet.')}</p>
                    ) : (
                        <div className="space-y-2" data-testid="ops-activity-list">
                            {activity.map((e, i) => (
                                <Card key={i}>
                                    <CardContent className="p-4 flex flex-wrap items-center justify-between gap-2">
                                        <div className="text-sm">
                                            {e.type === 'arrival' ? (
                                                <span><b>{e.staff_name || '—'}</b> {t('hotelOps.arrivedOn', 'arrived on')} {t('hotelRooms.floor', 'Floor')} {e.floor}</span>
                                            ) : (
                                                <span><b>{e.staff_name || '—'}</b> · {t('hotelOps.room', 'Room')} {e.room_number} · <span className={STATE_STYLE[e.prev_state]?.text}>{STATE_STYLE[e.prev_state]?.label || e.prev_state}</span> → <span className={STATE_STYLE[e.new_state]?.text}>{STATE_STYLE[e.new_state]?.label || e.new_state}</span>{e.reason ? ` · "${e.reason}"` : ''}</span>
                                            )}
                                            <p className="text-xs text-muted-foreground mt-0.5">{fmt(e.at)}</p>
                                        </div>
                                        {e.photo_ids?.length > 0 && (
                                            <div className="flex gap-2">
                                                {e.photo_ids.map((pid) => <OpsPhoto key={pid} orgId={orgId} photoId={pid} />)}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
                <DialogContent className="max-w-lg" data-testid="ops-history-modal">
                    <DialogHeader>
                        <DialogTitle>{t('hotelOps.room', 'Room')} {history?.room_number} · {t('hotelOps.history', 'History')}</DialogTitle>
                    </DialogHeader>
                    {history?.loading ? (
                        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
                    ) : (history?.history || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('hotelOps.noHistory', 'No history yet.')}</p>
                    ) : (
                        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                            {(history.history || []).map((h, i) => (
                                <div key={i} className="border-l-2 border-muted pl-3" data-testid={`ops-history-item-${i}`}>
                                    <p className="text-sm">
                                        <span className={STATE_STYLE[h.prev_state]?.text}>{STATE_STYLE[h.prev_state]?.label || h.prev_state}</span>
                                        {' → '}
                                        <span className={STATE_STYLE[h.new_state]?.text}>{STATE_STYLE[h.new_state]?.label || h.new_state}</span>
                                    </p>
                                    <p className="text-xs text-muted-foreground">{fmt(h.at)} · {h.staff_name || '—'}</p>
                                    {h.reason && <p className="text-xs mt-1"><b>{t('hotelOps.reason', 'Reason')}:</b> {h.reason}</p>}
                                    {h.photo_ids?.length > 0 && (
                                        <div className="flex gap-2 mt-2">
                                            {h.photo_ids.map((pid) => <OpsPhoto key={pid} orgId={orgId} photoId={pid} />)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default HotelOpsPortal;
